import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { AuraValidator, AuraConfig, AuraVerdict, AuraSignal } from '@/lib/aura-validator';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';

interface AuraValidationDashboardProps {
  className?: string;
}

export function AuraValidationDashboard({ className }: AuraValidationDashboardProps) {
  const [signals, setSignals] = useState<AuraSignal[]>([]);
  const [verdict, setVerdict] = useState<AuraVerdict | null>(null);
  const [config, setConfig] = useState<AuraConfig>({
    minCoherence: 0.72,
    minPurity: 0.72,
    requireAll: false
  });
  const [isScanning, setIsScanning] = useState(false);

  const runAuraValidation = () => {
    setIsScanning(true);
    
    // Simulate scanning delay
    setTimeout(() => {
      const newSignals = AuraValidator.synthesizeSignals();
      const newVerdict = AuraValidator.validateAura(
        newSignals.map(s => s.data),
        config
      );
      
      setSignals(newSignals);
      setVerdict(newVerdict);
      setIsScanning(false);
    }, 2000);
  };

  const getStatusColor = (validated: boolean) => {
    return validated ? 'bg-green-500' : 'bg-red-500';
  };

  const getStatusText = (validated: boolean) => {
    return validated ? 'VALIDATED' : 'REJECTED';
  };

  const formatChartData = (signal: AuraSignal) => {
    return signal.data.slice(0, 200).map((value, index) => ({
      index,
      value: value.toFixed(3)
    }));
  };

  return (
    <div className={`space-y-6 ${className}`}>
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-white">Aura Validation Pipeline</h2>
        <Button 
          onClick={runAuraValidation}
          disabled={isScanning}
          className="bg-purple-600 hover:bg-purple-700"
        >
          {isScanning ? 'Scanning...' : 'Run Aura Scan'}
        </Button>
      </div>

      {/* Configuration Panel */}
      <Card className="bg-gray-900 border-purple-500/30">
        <CardHeader>
          <CardTitle className="text-purple-300">Validation Configuration</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-sm text-gray-300">Min Coherence</label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="1"
                value={config.minCoherence}
                onChange={(e) => setConfig(prev => ({ ...prev, minCoherence: parseFloat(e.target.value) }))}
                className="w-full mt-1 p-2 bg-gray-800 border border-gray-600 rounded text-white"
              />
            </div>
            <div>
              <label className="text-sm text-gray-300">Min Purity</label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="1"
                value={config.minPurity}
                onChange={(e) => setConfig(prev => ({ ...prev, minPurity: parseFloat(e.target.value) }))}
                className="w-full mt-1 p-2 bg-gray-800 border border-gray-600 rounded text-white"
              />
            </div>
            <div className="flex items-center space-x-2 mt-6">
              <input
                type="checkbox"
                id="requireAll"
                checked={config.requireAll}
                onChange={(e) => setConfig(prev => ({ ...prev, requireAll: e.target.checked }))}
                className="rounded"
              />
              <label htmlFor="requireAll" className="text-sm text-gray-300">Require All</label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Validation Results */}
      {verdict && (
        <Card className="bg-gray-900 border-purple-500/30">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="text-purple-300">Validation Results</span>
              <Badge className={getStatusColor(verdict.validated)}>
                {getStatusText(verdict.validated)}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-6">
              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-gray-300">Phase Coherence</span>
                  <span className="text-white font-mono">{verdict.coherence.toFixed(3)}</span>
                </div>
                <Progress value={verdict.coherence * 100} className="h-2" />
                <div className="text-xs text-gray-400 mt-1">
                  Threshold: {config.minCoherence.toFixed(3)}
                </div>
              </div>
              
              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-gray-300">Average Purity</span>
                  <span className="text-white font-mono">{verdict.purityAvg.toFixed(3)}</span>
                </div>
                <Progress value={verdict.purityAvg * 100} className="h-2" />
                <div className="text-xs text-gray-400 mt-1">
                  Threshold: {config.minPurity.toFixed(3)}
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-medium text-gray-300 mb-2">Individual Signal Purity</h4>
              <div className="space-y-2">
                {signals.map((signal, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <span className="text-sm text-gray-400 w-24">{signal.name}</span>
                    <Progress value={verdict.purityEach[index] * 100} className="flex-1 h-1" />
                    <span className="text-xs font-mono text-white w-12">
                      {verdict.purityEach[index].toFixed(3)}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="text-xs text-gray-400">
              Coherence pairs analyzed: {verdict.details.pairs}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Signal Visualization */}
      {signals.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {signals.map((signal, index) => (
            <Card key={index} className="bg-gray-900 border-purple-500/30">
              <CardHeader>
                <CardTitle className="text-sm text-purple-300">{signal.name}</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={150}>
                  <LineChart data={formatChartData(signal)}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis hide />
                    <YAxis hide />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#1f2937', 
                        border: '1px solid #6b7280',
                        borderRadius: '4px'
                      }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="value" 
                      stroke="#8b5cf6" 
                      strokeWidth={1}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {isScanning && (
        <Card className="bg-gray-900 border-purple-500/30">
          <CardContent className="flex items-center justify-center py-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
              <p className="text-purple-300">Scanning aura signatures...</p>
              <p className="text-sm text-gray-400 mt-2">Analyzing coherence and purity patterns</p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}